import { graphql, StaticQuery, withPrefix } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import Helmet from 'react-helmet';
// import fonts
import 'typeface-source-sans-pro/index.css';
import 'typeface-space-mono/index.css';
import '../css/prism-coy.css';
import customChartApiSidebar from '../pages/docs/custom-chart-api/custom-chart-api-links.yaml';
import zoomdataClientiSidebar from '../pages/docs/zoomdata-client/zoomdata-client-links.yaml';
import customChartTutorialSidebar from '../pages/tutorials/custom-chart/tutorial-custom-chart-links.yml';
import presets, { colors } from '../utils/presets';
import { rhythm } from '../utils/typography';
import Footer from './footer';
import Navigation from './navigation';
import MobileNavigation from './navigation-mobile';
import SidebarBody from './sidebar-body';

const usePrefix = withPrefix('') !== `/`;

const DefaultLayout = ({ location, children }) => (
  <StaticQuery
    query={graphql`
      query {
        site {
          siteMetadata {
            title
            description
            twitter
          }
        }
        allCategoriesYaml {
          edges {
            node {
              fields {
                name
                slug
              }
            }
          }
        }
      }
    `}
    render={data => {
      const { allCategoriesYaml, site } = data;
      const prefixPattern = usePrefix
        ? `${withPrefix('').split('/')[1]}\\/`
        : '';
      const isHomepageRegExp = new RegExp(`^\\/(${prefixPattern}|)$`);
      const hasSidebarRegExp = new RegExp(
        `^\\/(${prefixPattern}|)(docs\\/(custom-chart-api|zoomdata-client)|tutorials\\/(custom-chart)|blog)\\/`,
      );
      const isSearchSourceRegExp = new RegExp(
        `^\\/(${prefixPattern}|)(docs|tutorials)\\/`,
      );
      const customChartApiRegExp = new RegExp(
        `^\\/(${prefixPattern}|)docs\\/custom-chart-api\\/`,
      );
      const zoomdataClientRegExp = new RegExp(
        `^\\/(${prefixPattern}|)docs\\/zoomdata-client\\/`,
      );
      const customChartTutorialRegExp = new RegExp(
        `^\\/(${prefixPattern}|)tutorials\\/custom-chart\\/`,
      );
      const blogRegExp = new RegExp(`^\\/(${prefixPattern}|)blog\\/`);
      const blogSidebar = [
        {
          items: [{ title: `All`, link: `/blog/` }].concat(
            allCategoriesYaml.edges.map(edge => ({
              title: edge.node.fields.name,
              link: edge.node.fields.slug,
            })),
          ),
          title: `Categories`,
        },
      ];
      const isHomepage = isHomepageRegExp.test(location.pathname);
      const hasSidebar = hasSidebarRegExp.test(location.pathname);
      const isSearchSource = isSearchSourceRegExp.test(location.pathname);
      const sidebarStyles = {
        order: -1,
        borderRight: `1px solid ${colors.ui.light}`,
        backgroundColor: colors.ui.whisper,
        flex: `0 0 ${rhythm(10)}`,
        display: `none`,
        top: `calc(${presets.headerHeight} - 1px)`,
        overflowY: `auto`,
        WebkitOverflowScrolling: `touch`,
        '::-webkit-scrollbar': {
          width: `6px`,
          height: `6px`,
        },
        '::-webkit-scrollbar-thumb': {
          background: colors.ui.bright,
        },
        '::-webkit-scrollbar-track': {
          background: colors.ui.light,
        },
        [presets.Desktop]: {
          flex: `0 0 ${rhythm(12)}`,
          padding: rhythm(1),
        },
      };

      return (
        <div
          css={{
            display: `flex`,
            minHeight: `100vh`,
            flexDirection: `column`,
          }}
        >
          <Helmet
            defaultTitle={`${site.siteMetadata.title}`}
            titleTemplate="%s | Zoomdata"
            title="Zoomdata Developers Zone"
          >
            <meta name="description" content={site.siteMetadata.description} />
            <meta name="twitter:site" content={site.siteMetadata.twitter} />
            <meta name="og:type" content="website" />
            <meta name="og:site_name" content={site.siteMetadata.title} />
            <html lang="en" />
          </Helmet>
          <Navigation pathname={location.pathname} />
          <div
            className={hasSidebar ? `main-body has-sidebar` : `main-body`}
            css={{
              display: `flex`,
              flex: 1,
              paddingTop: 0,
              [presets.Tablet]: {
                paddingTop: isHomepage ? 0 : presets.headerHeight,
              },
            }}
          >
            {/* TODO Move this under docs/index.js once Gatsby supports multiple levels
               of layouts */}
            <div
              css={{
                ...sidebarStyles,
                [presets.Tablet]: {
                  display: customChartApiRegExp.test(location.pathname)
                    ? `block`
                    : `none`,
                },
              }}
            >
              <SidebarBody yaml={customChartApiSidebar} />
            </div>
            {/* TODO Move this under docs/index.js once Gatsby supports multiple levels
               of layouts */}
            <div
              css={{
                ...sidebarStyles,
                [presets.Tablet]: {
                  display: zoomdataClientRegExp.test(location.pathname)
                    ? `block`
                    : `none`,
                },
              }}
            >
              <SidebarBody yaml={zoomdataClientiSidebar} />
            </div>
            {/* TODO Move this under docs/tutorial/index.js once Gatsby supports multiple levels
               of layouts */}
            <div
              css={{
                ...sidebarStyles,
                [presets.Tablet]: {
                  display: customChartTutorialRegExp.test(location.pathname)
                    ? `block`
                    : `none`,
                },
              }}
            >
              <SidebarBody yaml={customChartTutorialSidebar} />
            </div>
            <div
              css={{
                ...sidebarStyles,
                [presets.Tablet]: {
                  display: blogRegExp.test(location.pathname)
                    ? `block`
                    : `none`,
                },
              }}
            >
              <SidebarBody yaml={blogSidebar} />
            </div>
            <div
              css={{
                background: colors.ui.whisper,
                flex: 1,
              }}
              className={isSearchSource && `docSearch-content`}
            >
              {children}
            </div>
          </div>
          <Footer />
          <MobileNavigation />
        </div>
      );
    }}
  />
);

DefaultLayout.propTypes = {
  location: PropTypes.object,
  children: PropTypes.node,
};

export default DefaultLayout;
